import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import { alpha, keyframes } from "@mui/material/styles";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import pdf from "../../Assets/Rahman_Idrees_Resume_2026.pdf";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

function ResumeNew() {
  const [width, setWidth] = useState(1200);
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const pageWidth = Math.min(width - (width < 600 ? 48 : 160), 850);
  const changePage = (nextPage) => {
    setPageNumber(Math.min(Math.max(nextPage, 1), numPages || 1));
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "ArrowLeft") {
        setPageNumber((currentPage) => Math.max(currentPage - 1, 1));
      }
      if (event.key === "ArrowRight") {
        setPageNumber((currentPage) => Math.min(currentPage + 1, numPages || 1));
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [numPages]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 10, md: 12 },
        position: "relative",
      }}
    >
      {/* Background accent */}
      <Box
        sx={{
          position: "absolute",
          top: "30%",
          right: "-5%",
          width: "25%",
          height: "25%",
          background: "radial-gradient(circle, rgba(0, 127, 255, 0.08) 0%, transparent 70%)",
          filter: "blur(60px)",
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="lg">
        {/* Header */}
        <Box
          sx={{
            textAlign: "center",
            mb: 6,
            animation: `${fadeIn} 0.6s ease-out`,
          }}
        >
          {/* <Typography
            variant="overline"
            sx={{
              color: "primary.main",
              fontWeight: 600,
              letterSpacing: 3,
              mb: 1,
              display: "block",
            }}
          >
            Resume
          </Typography> */}
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 700,
              mb: 3,
              background: "linear-gradient(135deg, #ffffff 0%, #007FFF 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            My Resume
          </Typography>
          <Button
            component="a"
            href={pdf}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            startIcon={<DownloadIcon />}
            size="large"
            sx={{ px: 4, py: 1.5 }}
          >
            Download CV
          </Button>
        </Box>

        {/* PDF Viewer */}
        <Paper
          sx={{
            p: { xs: 1.5, sm: 3 },
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.5),
            borderRadius: 3,
            border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            boxShadow: (theme) => `0 24px 80px ${alpha(theme.palette.common.black, 0.35)}`,
            animation: `${fadeIn} 0.6s ease-out`,
            animationDelay: "0.2s",
            animationFillMode: "both",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mb: 2,
              px: { xs: 0.5, sm: 1 },
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Resume preview
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Use the arrows or your keyboard to explore
              </Typography>
            </Box>
            {numPages > 0 && (
              <Typography
                variant="body2"
                sx={{
                  flexShrink: 0,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 10,
                  color: "primary.light",
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12),
                }}
              >
                {pageNumber} / {numPages}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              position: "relative",
              display: "flex",
              justifyContent: "center",
              minHeight: { xs: 300, md: 600 },
              overflow: "hidden",
              borderRadius: 2,
              backgroundColor: (theme) => alpha(theme.palette.common.black, 0.28),
              "& .react-pdf__Page": {
                boxShadow: "0 12px 40px rgba(0, 0, 0, 0.35)",
                animation: `${fadeIn} 0.35s ease-out`,
              },
              "& .react-pdf__Page__canvas": {
                maxWidth: "100%",
                height: "auto !important",
              },
            }}
          >
          <Document
            file={pdf}
            onLoadSuccess={({ numPages: loadedPages }) => {
              setNumPages(loadedPages);
              setPageNumber(1);
            }}
            loading={
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 400,
                }}
              >
                <Typography color="text.secondary">Loading PDF...</Typography>
              </Box>
            }
          >
            <Page
              key={pageNumber}
              pageNumber={pageNumber}
              width={pageWidth}
              renderTextLayer={false}
            />
          </Document>
          </Box>

          {numPages > 1 && (
            <Box
              component="nav"
              aria-label="Resume page navigation"
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: { xs: 1.5, sm: 3 },
                mt: 2.5,
              }}
            >
              <IconButton
                aria-label="Previous resume page"
                onClick={() => changePage(pageNumber - 1)}
                disabled={pageNumber <= 1}
                sx={{
                  border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                }}
              >
                <ArrowBackRoundedIcon />
              </IconButton>

              <Box sx={{ display: "flex", gap: 1 }}>
                {Array.from({ length: numPages }, (_, index) => (
                  <Box
                    component="button"
                    key={index + 1}
                    aria-label={`Go to resume page ${index + 1}`}
                    aria-current={pageNumber === index + 1 ? "page" : undefined}
                    onClick={() => changePage(index + 1)}
                    sx={{
                      width: pageNumber === index + 1 ? 28 : 9,
                      height: 9,
                      p: 0,
                      border: 0,
                      borderRadius: 10,
                      cursor: "pointer",
                      transition: "all 0.25s ease",
                      backgroundColor:
                        pageNumber === index + 1 ? "primary.main" : "rgba(255,255,255,0.25)",
                    }}
                  />
                ))}
              </Box>

              <IconButton
                aria-label="Next resume page"
                onClick={() => changePage(pageNumber + 1)}
                disabled={pageNumber >= numPages}
                sx={{
                  border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                }}
              >
                <ArrowForwardRoundedIcon />
              </IconButton>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
}

export default ResumeNew;
